import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { PlaneIcon, LandmarkIcon, BuildingIcon, ShipIcon, HomeIcon, MapPinIcon, PhoneIcon, ArrowRightIcon } from 'lucide-react';
interface HotspotPageProps {
  onNavigate: (page: string) => void;
}
export function HotspotPage({
  onNavigate
}: HotspotPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const categories = [{
    id: 'airports',
    title: 'Airports',
    icon: PlaneIcon,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    locations: [{
      name: 'Ronald Reagan Washington National Airport',
      desc: 'Convenient delivery right to your terminal or baggage claim.',
      image: 'https://images.unsplash.com/photo-1529074963764-98f45c47344b?w=800&h=600&fit=crop'
    }, {
      name: 'Washington Dulles International Airport',
      desc: 'Seamless equipment handover for domestic and international arrivals.',
      image: 'https://images.unsplash.com/photo-1474302770737-173ee21bab63?w=800&h=600&fit=crop'
    }, {
      name: 'Baltimore/Washington International Airport (BWI)',
      desc: 'Start your trip comfortably from the moment you land.',
      image: 'https://images.unsplash.com/photo-1556388158-158ea5ccacbd?w=800&h=600&fit=crop'
    }]
  }, {
    id: 'attractions',
    title: 'City Attractions',
    icon: LandmarkIcon,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50',
    locations: [{
      name: 'The White House',
      desc: 'Explore the iconic residence and surrounding areas with ease.',
      image: 'https://images.unsplash.com/photo-1501466044931-62695aada8e9?w=800&h=600&fit=crop'
    }, {
      name: 'National Mall & Memorial Parks',
      desc: 'Tour the monuments comfortably on our reliable scooters.',
      image: 'https://images.unsplash.com/photo-1617581629397-a72507c3de9e?w=800&h=600&fit=crop'
    }, {
      name: 'Smithsonian Museums',
      desc: 'Navigate the expansive museum galleries without fatigue.',
      image: 'https://images.unsplash.com/photo-1564399579883-451a5d44ec08?w=800&h=600&fit=crop'
    }, {
      name: 'Lincoln Memorial',
      desc: "Access one of DC's most famous landmarks effortlessly.",
      image: 'https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?w=800&h=600&fit=crop'
    }, {
      name: 'U.S. Capitol Building',
      desc: 'Visit the heart of American government with full mobility.',
      image: 'https://images.unsplash.com/photo-1568515387631-8b650bbcdb90?w=800&h=600&fit=crop'
    }, {
      name: 'Arlington National Cemetery',
      desc: 'Pay your respects while navigating the vast grounds comfortably.',
      image: 'https://images.unsplash.com/photo-1508433957232-3107f5fd5995?w=800&h=600&fit=crop'
    }, {
      name: 'Georgetown Waterfront',
      desc: 'Enjoy shopping and dining along the scenic Potomac River.',
      image: 'https://images.unsplash.com/photo-1562932831-afcfe48b5786?w=800&h=600&fit=crop'
    }, {
      name: 'National Zoo',
      desc: 'Spend the whole day seeing the animals without getting tired.',
      image: 'https://images.unsplash.com/photo-1534567153574-2b12153a87f0?w=800&h=600&fit=crop'
    }]
  }, {
    id: 'hotels',
    title: 'Hotels & Resorts',
    icon: BuildingIcon,
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    locations: [{
      name: 'Major hotels in Washington D.C.',
      desc: 'Direct delivery to the bell desk or your room.',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop'
    }, {
      name: 'Hotels in Arlington, VA',
      desc: 'Serving all major accommodations across the river.',
      image: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800&h=600&fit=crop'
    }, {
      name: 'Hotels in Bethesda, MD',
      desc: 'Convenient drop-offs for our Maryland visitors.',
      image: 'https://images.unsplash.com/photo-1551882547-ff40c0d13c05?w=800&h=600&fit=crop'
    }, {
      name: 'Hotels in Baltimore, MD',
      desc: 'Full coverage for the Inner Harbor and surrounding areas.',
      image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&h=600&fit=crop'
    }, {
      name: 'Convention Centers',
      desc: 'Equipment waiting for you when you arrive for your event.',
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=600&fit=crop'
    }]
  }, {
    id: 'cruise',
    title: 'Cruise Ports',
    icon: ShipIcon,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-50',
    locations: [{
      name: 'Baltimore Cruise Terminal (Port of Baltimore)',
      desc: 'Start your vacation right with dockside delivery.',
      image: 'https://images.unsplash.com/photo-1548574505-5e239809ee19?w=800&h=600&fit=crop'
    }, {
      name: 'Cruise ships docked at Baltimore',
      desc: 'We coordinate directly with cruise lines for seamless boarding.',
      image: 'https://images.unsplash.com/photo-1580541631950-7282082b53ce?w=800&h=600&fit=crop'
    }]
  }, {
    id: 'residences',
    title: 'Residences & Other',
    icon: HomeIcon,
    color: 'text-purple-500',
    bgColor: 'bg-purple-50',
    locations: [{
      name: 'Private Residences',
      desc: 'Direct to your door delivery for locals needing temporary assistance.',
      image: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&h=600&fit=crop'
    }, {
      name: 'Airbnb & Vacation Rentals',
      desc: 'We deliver to your short-term rental property.',
      image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&h=600&fit=crop'
    }, {
      name: 'Assisted Living Facilities',
      desc: 'Providing mobility solutions for residents and visiting family.',
      image: 'https://images.unsplash.com/photo-1586105251261-72a756497a11?w=800&h=600&fit=crop'
    }, {
      name: 'Rehabilitation Centers',
      desc: 'Supporting your recovery journey with reliable equipment.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    }, {
      name: 'Event Venues',
      desc: 'Weddings, graduations, and family gatherings made accessible.',
      image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800&h=600&fit=crop'
    }]
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <main className="flex-grow w-full bg-[#f8f9fa]" data-id="element-1114">
      <PageHeader title="Delivery Hotspots" breadcrumb="Hotspots" onNavigateHome={() => onNavigate('home')} data-id="element-1115" />

      {/* Intro Section */}
      <section className="py-16 md:py-24 bg-white border-b border-gray-100 overflow-hidden" data-id="element-1116">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1117">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl mx-auto" data-id="element-1118">
            <div className="w-16 h-16 bg-brand-red/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1119">
              <MapPinIcon className="w-8 h-8 text-brand-red" data-id="element-1120" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-brand-navy mb-6 leading-tight" data-id="element-1121">
              We deliver mobility equipment{' '}
              <span className="text-brand-red" data-id="element-1122">FREE</span> to all these
              locations
            </h2>
            <p className="text-lg md:text-xl text-brand-charcoal/70 leading-relaxed" data-id="element-1123">
              Serving the entire D.C., Maryland, Virginia & Baltimore area.
              Whether you're arriving at the airport, staying at a hotel, or
              visiting iconic landmarks, we'll have your equipment waiting for
              you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      {categories.map((category, index) => <section key={category.id} className={`py-20 overflow-hidden ${index % 2 === 0 ? 'bg-[#f8f9fa]' : 'bg-white'}`} data-id="element-1124">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1125">
            <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="flex items-center gap-4 mb-12" data-id="element-1126">
              <div className={`w-14 h-14 rounded-2xl ${category.bgColor} flex items-center justify-center shrink-0 shadow-sm`} data-id="element-1127">
                <category.icon className={`w-7 h-7 ${category.color}`} data-id="element-1128" />
              </div>
              <h2 className="text-3xl font-bold text-brand-navy" data-id="element-1129">
                {category.title}
              </h2>
            </motion.div>

            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8" data-id="element-1130">
              {category.locations.map((location, locIndex) => <motion.div key={locIndex} variants={cardVariants} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow group flex flex-col h-full" data-id="element-1131">
                  <div className="h-48 overflow-hidden relative" data-id="element-1132">
                    <div className="absolute inset-0 bg-brand-navy/10 group-hover:bg-transparent transition-colors z-10" data-id="element-1133" />
                    <img src={location.image} alt={location.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1134" />
                  </div>
                  <div className="p-6 flex flex-col flex-grow" data-id="element-1135">
                    <h3 className="text-lg font-bold text-brand-navy mb-2 leading-snug" data-id="element-1136">
                      {location.name}
                    </h3>
                    <p className="text-sm text-brand-charcoal/70 leading-relaxed mt-auto" data-id="element-1137">
                      {location.desc}
                    </p>
                  </div>
                </motion.div>)}
            </motion.div>
          </div>
        </section>)}

      {/* Bottom CTA */}
      <section className="py-20 bg-brand-navy text-center overflow-hidden relative" data-id="element-1138">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-1139">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-brand-blue/10 rounded-full blur-3xl" data-id="element-1140"></div>
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-brand-red/10 rounded-full blur-3xl" data-id="element-1141"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 relative z-10" data-id="element-1142">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1143">
            Don't see your location?
          </h2>
          <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-1144">
            We likely deliver there too! Contact us to confirm delivery to your
            specific address or venue in the DMV area.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1145">
            <button onClick={() => onNavigate('reserve')} className="w-full sm:w-auto px-8 py-4 bg-brand-red text-white rounded-full font-bold text-lg hover:bg-red-700 transition-all shadow-lg flex items-center justify-center gap-2" data-id="element-1146">
              Reserve Now
              <ArrowRightIcon className="w-5 h-5" data-id="element-1147" />
            </button>
            <a href="tel:+18003954459" className="w-full sm:w-auto px-8 py-4 bg-transparent border-2 border-white/30 text-white rounded-full font-bold text-lg hover:bg-white hover:text-brand-navy transition-all flex items-center justify-center gap-2" data-id="element-1148">
              <PhoneIcon className="w-5 h-5" data-id="element-1149" />
              Call (800) 395-4459
            </a>
          </div>
        </div>
      </section>
    </main>;
}